import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import gsap from 'gsap';
import { ArrowRight, ChevronDown, Paperclip, Mic, GraduationCap, Send } from 'lucide-react';

const brands = [
  { company: 'Zara', category: 'retailer' },
  { company: 'Amazon', category: 'online store' },
  { company: 'Dominos', category: 'pizza chain' },
  { company: 'Crumbl', category: 'cookie shop' },
  { company: 'Nike', category: 'clothing brand' },
  { company: 'Home Depot', category: 'home improvement retailer' },
  { company: 'Marriott', category: 'hospitality company' },
  { company: 'Starbucks', category: 'coffee shop' },
  { company: 'Disney', category: 'entertainment company' },
];

const Hero = () => {
  const navigate = useNavigate();
  const heroRef = useRef<HTMLDivElement>(null);
  const statsRef = useRef<HTMLDivElement>(null);
  const ctaRef = useRef<HTMLDivElement>(null);
  const cycleRef = useRef<HTMLDivElement>(null);
  const promptRef = useRef<HTMLDivElement>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [displayCompany, setDisplayCompany] = useState('');
  const [displayIs, setDisplayIs] = useState('');
  const [displayText, setDisplayText] = useState('');
  const [displayPeriod, setDisplayPeriod] = useState('');
  const [showCursor, setShowCursor] = useState(true);
  const [isAnimating, setIsAnimating] = useState(false);
  const [phase, setPhase] = useState<'showing' | 'backspacing' | 'typing'>('showing');

  // Initialize display
  useEffect(() => {
    const brand = brands[currentIndex];
    setDisplayCompany(brand.company);
    setDisplayIs('is');
    setDisplayText(`not a ${brand.category}`);
    setDisplayPeriod('.');
  }, []);

  // Cycling animation for brand statements
  useEffect(() => {
    if (isAnimating) return;
    
    const cycleTimeout = setTimeout(() => {
      const currentBrand = brands[currentIndex];
      const fullDisplayText = `not a ${currentBrand.category}`;
      const targetText = 'a tech company';
      
      setIsAnimating(true);
      setShowCursor(true);
      setPhase('backspacing');
      
      // Step 1: Backspace period first
      setDisplayPeriod('');
      
      // Step 2: Backspace the changing text ("not a [category]")
      let backspaceIndex = fullDisplayText.length;
      const backspaceInterval = setInterval(() => {
        if (backspaceIndex > 0) {
          backspaceIndex--;
          setDisplayText(fullDisplayText.slice(0, backspaceIndex));
        } else {
          clearInterval(backspaceInterval);
          // Small pause
          setTimeout(() => {
            // Step 3: Type "a tech company"
            setPhase('typing');
            let typeIndex = 0;
            const typeInterval = setInterval(() => {
              if (typeIndex < targetText.length) {
                typeIndex++;
                setDisplayText(targetText.slice(0, typeIndex));
              } else {
                clearInterval(typeInterval);
                // Type period after "a tech company"
                setDisplayPeriod('.');
                setShowCursor(false);
                // Step 3: After showing "tech company", backspace everything and retype new brand
                setTimeout(() => {
                  setPhase('backspacing');
                  setShowCursor(true);
                  
                  // Backspace period first
                  setDisplayPeriod('');
                  
                  // Backspace "a tech company"
                  let bsIndex = targetText.length;
                  const bsInterval = setInterval(() => {
                    if (bsIndex > 0) {
                      bsIndex--;
                      setDisplayText(targetText.slice(0, bsIndex));
                    } else {
                      clearInterval(bsInterval);
                      // Backspace "is"
                      let isBsIndex = 2; // "is" length
                      const isBsInterval = setInterval(() => {
                        if (isBsIndex > 0) {
                          isBsIndex--;
                          setDisplayIs('is'.slice(0, isBsIndex));
                        } else {
                          clearInterval(isBsInterval);
                          // Backspace company name
                          let companyBsIndex = currentBrand.company.length;
                          const companyBsInterval = setInterval(() => {
                            if (companyBsIndex > 0) {
                              companyBsIndex--;
                              setDisplayCompany(currentBrand.company.slice(0, companyBsIndex));
                            } else {
                              clearInterval(companyBsInterval);
                              // Small pause before typing new brand
                              setTimeout(() => {
                                setPhase('typing');
                                const nextIndex = (currentIndex + 1) % brands.length;
                                const nextBrand = brands[nextIndex];
                                
                                // Type company name
                                let companyTypeIndex = 0;
                                const companyTypeInterval = setInterval(() => {
                                  if (companyTypeIndex < nextBrand.company.length) {
                                    companyTypeIndex++;
                                    setDisplayCompany(nextBrand.company.slice(0, companyTypeIndex));
                                  } else {
                                    clearInterval(companyTypeInterval);
                                    // Type "is"
                                    let isTypeIndex = 0;
                                    const isTypeInterval = setInterval(() => {
                                      if (isTypeIndex < 2) {
                                        isTypeIndex++;
                                        setDisplayIs('is'.slice(0, isTypeIndex));
                                      } else {
                                        clearInterval(isTypeInterval);
                                        // Type "not a [category]"
                                        const newText = `not a ${nextBrand.category}`;
                                        let textTypeIndex = 0;
                                        const textTypeInterval = setInterval(() => {
                                          if (textTypeIndex < newText.length) {
                                            textTypeIndex++;
                                            setDisplayText(newText.slice(0, textTypeIndex));
                                          } else {
                                            clearInterval(textTypeInterval);
                                            // Type period
                                            setDisplayPeriod('.');
                                            setShowCursor(true);
                                            setPhase('showing');
                                            setCurrentIndex(nextIndex);
                                            setIsAnimating(false);
                                          }
                                        }, 60);
                                      }
                                    }, 60);
                                  }
                                }, 60);
                              }, 300);
                            }
                          }, 50);
                        }
                      }, 50);
                    }
                  }, 50);
                }, 2000);
              }
            }, 80);
          }, 400);
        }
      }, 50);
      
      return () => {
        clearInterval(backspaceInterval);
      };
    }, 3000); // Wait 3 seconds before starting animation

    return () => clearTimeout(cycleTimeout);
  }, [currentIndex, isAnimating]);


  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ defaults: { ease: 'expo.out' } });

      // Initial animation for cycling brand statement
      const lineElements = cycleRef.current?.querySelectorAll('.line-text');
      if (lineElements) {
        tl.fromTo(
          lineElements,
          { y: 30, opacity: 0 },
          { 
            y: 0, 
            opacity: 1,
            duration: 0.8, 
            ease: 'expo.out', 
            stagger: 0.05
          },
          0.2
        );
      }

      // Prompt input animation
      tl.fromTo(
        promptRef.current,
        { y: 30, opacity: 0, scale: 0.95 },
        { y: 0, opacity: 1, scale: 1, duration: 0.7, ease: 'expo.out' },
        0.8
      );

      // Stats animation
      const stats = statsRef.current?.querySelectorAll('.stat-item');
      if (stats) {
        tl.fromTo(
          stats,
          { y: 40, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.5, stagger: 0.1 },
          0.8
        );
      }

      // CTA buttons animation
      const buttons = ctaRef.current?.querySelectorAll('button');
      if (buttons) {
        tl.fromTo(
          buttons,
          { y: 30, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.5, stagger: 0.1 },
          1
        );
      }

    }, heroRef);

    return () => ctx.revert();
  }, []);

  const scrollToContact = () => {
    // If we're on a different route, navigate to home first, then scroll
    const currentPath = window.location.pathname;
    if (currentPath !== '/') {
      navigate('/');
      setTimeout(() => {
        const contactSection = document.querySelector('#contact');
        if (contactSection) {
          contactSection.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      const contactSection = document.querySelector('#contact');
      if (contactSection) {
        contactSection.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const stats: Array<{ icon: any; value: string; label: string }> = [
    // { icon: Database, value: '10x', label: 'More Data Discovered' },
    // { icon: TrendingUp, value: '3x', label: 'Revenue Increase' },
    // { icon: Zap, value: 'A-Z', label: 'Built In-House' },
  ];

  return (
    <section
      ref={heroRef}
      className="relative min-h-screen flex items-start justify-center bg-kmg-bg overflow-hidden"
    >
      {/* Video Background */}
      <video
        autoPlay
        loop
        muted
        playsInline
        className="absolute inset-0 w-full h-full object-cover z-0"
      >
        <source src="/hero.mov" type="video/quicktime" />
        <source src="/hero.mov" type="video/mp4" />
        Your browser does not support the video tag.
      </video>

      {/* Background Pattern */}
      <div className="absolute inset-0 grid-pattern opacity-30 z-[2]" />
      
      {/* Gradient Orbs */}
      <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-kmg-primary/5 rounded-full blur-[120px] z-[2]" />
      <div className="absolute bottom-1/4 left-1/4 w-80 h-80 bg-kmg-accent/5 rounded-full blur-[100px] z-[2]" />

      <div className="relative z-10 max-w-[95vw] mx-auto px-6 sm:px-8 lg:px-12 pt-48 sm:pt-40 pb-24 sm:pb-20">
        <div className="w-full mx-auto">
          {/* Content */}
          <div className="text-center flex flex-col items-center">
            {/* Main Big Slogan */}
            <h1 className="text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-bold text-slate-900 mb-20 sm:mb-16 leading-tight">
              Scale like a <span className="text-gradient">tech company</span>
            </h1>

            {/* Prompt Input Line */}
            <div ref={promptRef} className="mb-10 sm:mb-8 w-full flex justify-center">
              <div className="relative w-full max-w-[700px] bg-white border border-slate-200 rounded-[2rem] shadow-lg p-4 sm:p-6">
                {/* Main prompt text */}
                <div className="mb-4" style={{ minHeight: '32px' }}>
                  <p className="text-lg sm:text-xl text-slate-700 font-medium text-left flex items-center">
                    <span>How do I become one?</span>
                    <span className="inline-block w-0.5 h-5 sm:h-6 bg-slate-700 ml-0.5 animate-[blink_1s_step-end_infinite]"></span>
                  </p>
                </div>
                
                {/* Bottom row with icons and tags */}
                <div className="flex items-center justify-between flex-wrap gap-3">
                  {/* Left side - attachment and tags */}
                  <div className="flex items-center gap-3 flex-wrap">
                    <button className="w-8 h-8 flex items-center justify-center rounded-full border border-slate-200 hover:border-slate-300 hover:bg-slate-50 transition-colors">
                      <Paperclip className="w-4 h-4 text-slate-600" />
                    </button>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="px-3 py-1 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-full">
                        Company Insights
                      </span>
                    </div>
                  </div>
                  
                  {/* Right side - action icons */}
                  <div className="flex items-center gap-2">
                    <button className="w-8 h-8 flex items-center justify-center rounded-full border border-slate-200 hover:border-slate-300 hover:bg-slate-50 transition-colors">
                      <GraduationCap className="w-4 h-4 text-slate-600" />
                    </button>
                    <button className="w-8 h-8 flex items-center justify-center rounded-full border border-slate-200 hover:border-slate-300 hover:bg-slate-50 transition-colors">
                      <Mic className="w-4 h-4 text-slate-600" />
                    </button>
                    <button
                      onClick={scrollToContact}
                      className="w-10 h-10 flex items-center justify-center rounded-full bg-kmg-primary text-white hover:bg-kmg-primary-dark transition-colors shadow-md hover:shadow-lg"
                      aria-label="Contact us"
                    >
                      <Send className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* CTA Button */}
            <div ref={ctaRef} className="flex justify-center mb-10 sm:mb-8">
              <button
                onClick={scrollToContact}
                className="group px-8 py-4 text-base font-semibold text-white bg-kmg-primary rounded-full hover:bg-kmg-primary-dark hover:shadow-glow-lg transition-all duration-300 ease-expo-out hover:scale-105 flex items-center justify-center gap-2"
              >
                Get Started
                <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </div>

            {/* Cycling Brand Statement - Smaller, below prompt */}
            <div 
              ref={cycleRef}
              className="mb-14 sm:mb-12 flex items-center justify-center w-full"
            >
              {/* Smaller animated slogan */}
              <div className="relative h-12 sm:h-14 lg:h-16 w-full flex items-center justify-center overflow-visible">
                <div className="flex items-center justify-center gap-2 text-xl sm:text-2xl lg:text-3xl font-medium text-slate-600 whitespace-nowrap px-8" style={{ lineHeight: '1.2', alignItems: 'baseline' }}>
                  {/* Company name - Changes with typing animation */}
                  <span className="line-text inline-block text-kmg-primary flex-shrink-0" style={{ lineHeight: '1.2' }}>
                    {displayCompany}
                    {phase === 'typing' && displayCompany.length > 0 && displayIs.length === 0 && displayText.length === 0 && displayCompany.length < brands[(currentIndex + 1) % brands.length]?.company.length && (
                      <span className="inline-block w-0.5 h-[0.9em] bg-slate-600 ml-1 align-middle animate-[blink_1s_step-end_infinite]" style={{ flexShrink: 0 }}></span>
                    )}
                  </span>
                  {/* Middle section - "is" and changing text */}
                  <div className="flex items-center gap-2" style={{ alignItems: 'baseline' }}>
                    {/* "is" - Gets backspaced and retyped */}
                    <span className="line-text inline-block flex-shrink-0" style={{ lineHeight: '1.2' }}>
                      {displayIs}
                      {phase === 'typing' && displayCompany.length > 0 && displayIs.length > 0 && displayIs.length < 2 && displayText.length === 0 && (
                        <span className="inline-block w-0.5 h-[0.9em] bg-slate-600 ml-1 align-middle animate-[blink_1s_step-end_infinite]" style={{ flexShrink: 0 }}></span>
                      )}
                    </span>
                    {/* Changing text area */}
                    <span className="changing-text line-text inline-block flex-shrink-0 relative overflow-visible italic text-center sm:text-left sm:min-w-[220px] sm:max-w-[400px] inline-flex items-baseline" style={{ lineHeight: '1.2' }}>
                      <span className={displayText.includes('tech company') ? 'text-gradient' : 'text-slate-500'} style={{ whiteSpace: 'nowrap', lineHeight: '1.2' }}>
                        {displayText}
                        {displayPeriod}
                      </span>
                      {/* Blinking cursor */}
                      {showCursor && phase === 'typing' && displayText.length > 0 && (
                        <span className="inline-block w-0.5 h-[0.9em] bg-slate-600 ml-1 align-middle animate-[blink_1s_step-end_infinite]" style={{ flexShrink: 0 }}></span>
                      )}
                      {showCursor && phase === 'backspacing' && displayText.length > 0 && (
                        <span className="inline-block w-0.5 h-[0.9em] bg-slate-600 ml-1 align-middle animate-[blink_1s_step-end_infinite]" style={{ flexShrink: 0 }}></span>
                      )}
                      {phase === 'typing' && displayPeriod.length === 0 && displayText.length > 0 && displayText === `not a ${brands[(currentIndex + 1) % brands.length]?.category}` && (
                        <span className="inline-block w-0.5 h-[0.9em] bg-slate-600 ml-1 align-middle animate-[blink_1s_step-end_infinite]" style={{ flexShrink: 0 }}></span>
                      )}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Stats */}
            <div ref={statsRef} className="flex flex-wrap justify-center gap-6 mb-10">
              {stats.map((stat, index) => {
                const Icon = stat.icon;
                return (
                  <div
                    key={index}
                    className="stat-item flex items-center gap-3 px-4 py-3 bg-white rounded-xl shadow-card"
                  >
                    <div className="w-10 h-10 flex items-center justify-center bg-kmg-primary/10 rounded-lg">
                      <Icon className="w-5 h-5 text-kmg-primary" />
                    </div>
                    <div>
                      <p className="text-xl font-bold text-slate-900">{stat.value}</p>
                      <p className="text-xs text-slate-500">{stat.label}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 pb-2">
        <span className="text-xs font-medium text-slate-400 tracking-widest uppercase">
          Discover More
        </span>
        <ChevronDown className="w-5 h-5 text-slate-400 animate-bounce-arrow" />
      </div>
    </section>
  );
};

export default Hero;
